import React, { useState, useEffect } from 'react';
import { Post } from './types';
import { ErrorBoundary } from '../shared/ErrorBoundary';
import { PLATFORM_CONFIGS } from './platformConfigs';
import { useLiveEditor } from '../../contexts/LiveEditorContext';
import { useTranslation } from 'react-i18next';

interface Props {
  posts: Post[];
  platform: string;
}

// Define regex patterns as constants
const HASHTAG_REGEX = /#[\w\u0590-\u05ff]+/g;
const URL_REGEX = /\[([^\]]+)\]\(([^)]+)\)/g;
const EMOJI_REGEX =
  /([\u2700-\u27BF]|[\uE000-\uF8FF]|\uD83C[\uDC00-\uDFFF]|\uD83D[\uDC00-\uDFFF]|[\u2011-\u26FF]|\uD83E[\uDD10-\uDDFF])/g;

const PostCard: React.FC<{ post: Post; platform: string }> = ({ post, platform }) => {
  const { t } = useTranslation();
  const { appendToEditor, editedResult } = useLiveEditor();
  const [hoveredParagraph, setHoveredParagraph] = useState<string | null>(null);
  const [addedParagraphs, setAddedParagraphs] = useState<string[]>([]);

  // Listen for when the editor is cleared
  useEffect(() => {
    if (!editedResult.trim()) {
      setAddedParagraphs([]);
    }
  }, [editedResult]);

  const platformKey = platform.toLowerCase();
  const config = PLATFORM_CONFIGS[platformKey] || PLATFORM_CONFIGS.facebook;

  // Extract hashtags, links and format content
  const formatContent = (content: string) => {
    let processedContent = content;
    const hashtags: string[] = [];
    let lastPart = '';

    // Extract hashtags and URLs
    const parts = content.split('\n\n');
    if (parts.length > 1) {
      lastPart = parts.pop() || '';
      processedContent = parts.join('\n\n');
    }

    // Extract hashtags from the last part
    const hashtagMatches = lastPart.match(HASHTAG_REGEX) || [];
    hashtagMatches.forEach((tag) => {
      hashtags.push(tag);
    });

    // Format URLs
    processedContent = processedContent.replace(URL_REGEX, (match, text, url) => {
      return `<a href="${url}" class="${config.linkColor} hover:underline" target="_blank">${text}</a>`;
    });

    // Format emojis
    processedContent = processedContent.replace(
      EMOJI_REGEX,
      (match) => `<span class="inline-block">${match}</span>`
    );

    return { processedContent, hashtags, lastPart };
  };

  const { processedContent, hashtags, lastPart } = formatContent(post.content);

  const handleAddToEditor = (paragraphText: string) => {
    appendToEditor(paragraphText);
    setAddedParagraphs((prev) => [...prev, paragraphText]);
  };

  // Split content into paragraphs for hover functionality
  const paragraphs = processedContent
    .replace(/^```html\n/, '')
    .replace(/\n```$/, '')
    .trim()
    .split('\n\n');

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden max-w-2xl">
      {/* Header */}
      <div className={`p-4 ${config.headerColor} text-white flex items-center gap-2`}>
        <img src={config.icon} alt={config.name} className="w-6 h-6" />
        <span className="font-semibold">{config.name}</span>
      </div>

      {/* Content */}
      <div className="p-6">
        {/* Main content */}
        <div className="space-y-4">
          {paragraphs.map((paragraph, index) => (
            <div
              key={index}
              className={`text-[15px] leading-relaxed whitespace-pre-wrap relative p-1 border ${
                addedParagraphs.includes(paragraph)
                  ? 'border-green-500'
                  : hoveredParagraph === paragraph
                    ? 'border-blue-300'
                    : 'border-transparent'
              } rounded-md`}
              onMouseEnter={() => setHoveredParagraph(paragraph)}
              onMouseLeave={() => setHoveredParagraph(null)}
            >
              <div dangerouslySetInnerHTML={{ __html: paragraph }} />
              {hoveredParagraph === paragraph && (
                <button
                  className="absolute top-1 right-1 bg-blue-500 text-white text-xs px-2 py-1 rounded hover:bg-blue-600 flex items-center gap-1"
                  onClick={() => handleAddToEditor(paragraph)}
                >
                  {addedParagraphs.includes(paragraph) && <span className="text-white">✓</span>}
                  {t('common.add_to_live_editor')}
                </button>
              )}
            </div>
          ))}

          {/* Hashtags and call-to-action */}
          {lastPart && (
            <div className={`${config.hashtagColor} space-x-1`}>
              {hashtags.map((tag, i) => (
                <span key={i} className="hover:underline cursor-pointer">
                  {tag}
                </span>
              ))}
              {lastPart.replace(HASHTAG_REGEX, '').trim() && (
                <span
                  className={`${config.linkColor} cursor-pointer hover:underline`}
                  dangerouslySetInnerHTML={{
                    __html: lastPart
                      .replace(HASHTAG_REGEX, '')
                      .replace(/^```html\n/, '')
                      .replace(/\n```$/, '')
                      .trim(),
                  }}
                />
              )}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="mt-4 pt-4 border-t border-gray-200 flex gap-4">
          {config.actions.map((action, i) => (
            <button key={i} className="flex items-center gap-2 text-gray-600 hover:text-gray-800">
              <span>{action.icon}</span>
              <span className="text-sm">{action.label}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export const GeneratedPosts: React.FC<Props> = ({ posts, platform }) => {
  return (
    <section className="mt-6">
      <h2 className="text-xl font-semibold text-gray-700 dark:text-gray-200 mb-4">
        Generated Posts
      </h2>
      <div className="space-y-6">
        {posts.map((post, index) => (
          <ErrorBoundary
            key={index}
            fallback={
              <div className="p-4 border border-gray-200 rounded-md">
                <p className="whitespace-pre-wrap">{post.content}</p>
              </div>
            }
          >
            <PostCard post={post} platform={platform} />
          </ErrorBoundary>
        ))}
      </div>
    </section>
  );
};
